import { useState, type FormEvent } from "react";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Textarea } from "../ui/input";
import { formatDateTime, toMs } from "../../lib/format";
import { CRM_ACTIVITY_LABELS, formatRelativeDay } from "../../lib/crm";
import { useToastStore } from "../../stores/toast";
import type { CrmActivity, CrmActivityType } from "@shared/types";

// Au-delà, on replie : un compte rendu importé fait des milliers de caractères et
// noierait à lui seul la colonne latérale, présente sur tous les onglets.
const CLAMP_LENGTH = 400;

/** Corps d'une entrée : replié tant qu'il dépasse, avec un dépliage explicite. */
function FeedBody({ body }: { body: string }) {
  const [open, setOpen] = useState(false);
  const long = body.length > CLAMP_LENGTH;

  if (!long) return <p className="crm-feed__body">{body}</p>;

  return (
    <div className="crm-feed__long">
      <p className={`crm-feed__body ${open ? "" : "crm-feed__body--clamp"}`}>{body}</p>
      <button type="button" className="crm-feed__more" onClick={() => setOpen(!open)}>
        {open ? "Replier" : "Voir tout le compte rendu"}
      </button>
    </div>
  );
}

// Trois pastilles seulement : ce que l'utilisateur a écrit, ce que le dossier a
// changé de lui-même, et le reste (suivis, RDV, revenus) journalisé par l'API.
function dotModifier(type: CrmActivityType): string {
  if (type === "note") return "crm-feed__dot--note";
  if (type === "status_change" || type === "field_change") return "crm-feed__dot--status";
  return "crm-feed__dot--system";
}

interface ActivityFeedProps {
  accountId: string;
  items: CrmActivity[];
  users: Array<{ id: string; name: string }>;
  onChanged: () => void;
  title?: string;
  /** Nombre d'entrées affichées ; toutes par défaut. */
  limit?: number;
  showComposer?: boolean;
}

export function ActivityFeed({
  accountId,
  items,
  users,
  onChanged,
  title = "Historique",
  limit,
  showComposer = true,
}: ActivityFeedProps) {
  const push = useToastStore((s) => s.push);
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);

  const addNote = async (e: FormEvent) => {
    e.preventDefault();
    if (!body.trim() || saving) return;
    setSaving(true);
    try {
      await api(`/api/crm/accounts/${accountId}/activities`, {
        method: "POST",
        body: JSON.stringify({ body: body.trim() }),
      });
      setBody("");
      push("success", "Note ajoutée");
      onChanged();
    } catch {
      push("danger", "Échec de l'ajout de la note");
    } finally {
      setSaving(false);
    }
  };

  const sorted = [...items].sort((a, b) => toMs(b.createdAt) - toMs(a.createdAt));
  const shown = limit === undefined ? sorted : sorted.slice(0, limit);

  return (
    <section className="crm-section">
      <div className="crm-section__head">
        <h2 className="crm-section__title">{title}</h2>
      </div>

      {showComposer ? (
        <form className="crm-form" onSubmit={addNote}>
          <Textarea
            rows={3}
            placeholder="Ajouter une note — compte rendu d'appel, contexte, décision…"
            aria-label="Nouvelle note"
            value={body}
            onChange={(e) => setBody(e.target.value)}
          />
          <div className="crm-form__actions">
            <Button type="submit" size="sm" disabled={saving || !body.trim()}>
              {saving ? "Ajout…" : "Ajouter la note"}
            </Button>
          </div>
        </form>
      ) : null}

      {shown.length === 0 ? (
        <p className="crm-empty">Aucune activité pour l'instant.</p>
      ) : (
        <ol className="crm-feed">
          {shown.map((a) => (
            <li key={a.id} className="crm-feed__item">
              <span className={`crm-feed__dot ${dotModifier(a.type)}`} aria-hidden="true" />
              <div className="crm-feed__content">
                <FeedBody body={a.body} />
                <p className="crm-feed__meta">
                  <span>{CRM_ACTIVITY_LABELS[a.type]}</span>
                  {a.authorUserId ? (
                    <span>
                      {users.find((u) => u.id === a.authorUserId)?.name ?? "Utilisateur retiré"}
                    </span>
                  ) : null}
                  <span title={formatDateTime(a.createdAt)}>{formatRelativeDay(a.createdAt)}</span>
                </p>
              </div>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}
